import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { VirtualItem } from '@tanstack/react-virtual'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { BarDragApi } from '../hooks/useBarDrag'
import { BAR_H, ROW_H } from '../lib/constants'
import { isWeekend, type ISODate } from '../lib/format'
import { barBox, dateAt, rowY, timelineDays, visibleDayWindow, xOf } from '../lib/geometry'
import type { Tree } from '../lib/tree'
import { useGanttStore, useGanttStoreApi } from '../store/ganttStore'
import type { ScheduleActivity, ScheduleDetail, ScheduleExternalActivity } from '../types'
import ActivityBar from './ActivityBar'
import DependencyLayer from './DependencyLayer'
import { GanttHoverDispatchContext } from './gantt-hover'
import { useGanttLayout } from './gantt-layout'
import GhostMarker from './timeline/GhostMarker'
import TodayLine from './timeline/TodayLine'

interface Props {
  virtualItems: VirtualItem[]
  detail: ScheduleDetail
  byId: Map<string, ScheduleActivity>
  externalById: Map<string, ScheduleExternalActivity>
  canEdit: boolean
  tree: Tree<ScheduleActivity>
  /** External activities to ghost on a row, keyed by the local activity id that hosts them (§8.6). */
  ghostsByRow: Map<string, ScheduleExternalActivity[]>
  onGhostClick: (ext: ScheduleExternalActivity) => void
  /** Task 18: the bar-resize / link-drag lifecycle, hoisted into `GanttView` so it can be shared
   * with anything else that eventually needs it; this component only routes pointer events into it
   * and renders its live preview. */
  barDrag: BarDragApi
}

const PAN_CLICK_THRESHOLD_PX = 4

/** A pan gesture may start on the SVG itself or a background shape — never on a bar, a ghost, a
 * link, or a Task 18 drag handle (those start a resize/link drag instead, routed separately in
 * `onPointerDown` before this guard is even consulted). */
const isPannableTarget = (target: EventTarget | null): boolean =>
  !(target instanceof Element) || !target.closest('[data-bar], [data-ghost], [data-link], [data-resize-handle], [data-link-handle]')

/**
 * The timeline body (spec B §8.1–8.4, §8.7): weekend shading, bottom-tier
 * gridlines, row separators and selection bands, the today/status-date lines,
 * and one bar/milestone/summary per virtual row with its optional baseline
 * overlay and constraint pin. A drag on the empty background pans the scroller;
 * a drag under the click threshold clears the selection instead (Step 5).
 * Dependency lines and cross-schedule ghosts (spec B §8.5, §8.6) are drawn last,
 * after the bars — links in `<DependencyLayer>` first, ghosts last (fix round 1,
 * BUG-SCH-UI2-01): a link that terminates inside a ghost's box needs the ghost's
 * own hit area on top, or its invisible hit-stroke wins the pointer hit test over
 * the ghost everywhere the two overlap. Task 18's resize/link drag preview is
 * drawn last of all, on top of everything else.
 */
const GanttTimeline: React.FC<Props> = ({ virtualItems, detail, byId, externalById, canEdit, tree, ghostsByRow, onGhostClick, barDrag }) => {
  const { t } = useTranslation()
  const { rows, rowIndexById, range, pxPerDay, gridWidth, viewportWidth, viewportHeight, scrollLeft, scrollTop, today, scrollerRef, tiers } = useGanttLayout()
  const zoom = useGanttStore((s) => s.zoom)
  const showBaseline = useGanttStore((s) => s.showBaseline) && !!detail.schedule.activeBaselineId
  const showCriticalPath = useGanttStore((s) => s.showCriticalPath)
  const collapsedIds = useGanttStore((s) => s.collapsedIds)
  const selectedIds = useGanttStore((s) => s.selectedIds)
  // Write-only: this component draws every bar, so subscribing to the hover *value* would
  // re-render it on every pointermove (the Task 9 review finding). See gantt-hover.ts.
  const setHover = useContext(GanttHoverDispatchContext)
  const api = useGanttStoreApi()
  const width = Math.max(0, viewportWidth - gridWidth)
  const { fromDay, toDay } = visibleDayWindow(scrollLeft, width, pxPerDay, timelineDays(range.totalDays, pxPerDay))
  const contentTop = scrollTop
  const contentBottom = scrollTop + viewportHeight
  const selected = useMemo(() => new Set(selectedIds), [selectedIds])
  const visibleOrder = useMemo(() => rows.map((r) => r.node.activity.id), [rows])

  const days = useMemo(() => {
    const out: { day: number; iso: ISODate }[] = []
    for (let d = fromDay; d <= toDay; d++) out.push({ day: d, iso: dateAt(d, range.start) })
    return out
  }, [fromDay, toDay, range.start])
  // Review fix (Minor #11): `tiers` (locale-aware, both top and bottom) now comes from
  // `GanttLayout` — computed once in `GanttView` and shared with `GanttTimelineHeader` instead of
  // each rebuilding it (this component only ever needed `tiers.bottom`'s gridline x positions).

  const [panning, setPanning] = useState(false)
  const panRef = useRef<{ pointerId: number; startX: number; startScrollLeft: number } | null>(null)

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return
    if ((e.target as Element).closest('[data-resize-handle], [data-link-handle]')) {
      barDrag.onPointerDown(e)
      return
    }
    if (!isPannableTarget(e.target)) return
    const scroller = scrollerRef.current
    if (!scroller) return
    panRef.current = { pointerId: e.pointerId, startX: e.clientX, startScrollLeft: scroller.scrollLeft }
    e.currentTarget.setPointerCapture(e.pointerId)
    setPanning(true)
  }, [scrollerRef, barDrag])

  const onPointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (barDrag.drag) { barDrag.onPointerMove(e); return }
    const p = panRef.current
    if (!p || p.pointerId !== e.pointerId) return
    const scroller = scrollerRef.current
    if (!scroller) return
    scroller.scrollLeft = p.startScrollLeft - (e.clientX - p.startX)
  }, [scrollerRef, barDrag])

  const endPan = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const p = panRef.current
    if (!p || p.pointerId !== e.pointerId) return
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    const dragged = Math.abs(e.clientX - p.startX) >= PAN_CLICK_THRESHOLD_PX
    panRef.current = null
    setPanning(false)
    if (!dragged) api.getState().clearSelection() // also clears `selectedLinkId` (store's clearSelection)
  }, [api])

  const onPointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (barDrag.drag) { barDrag.onPointerUp(e); return }
    endPan(e)
  }, [barDrag, endPan])

  const onPointerCancel = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (barDrag.drag) { barDrag.onPointerCancel(e); return }
    endPan(e)
  }, [barDrag, endPan])

  const resizeRowTop = barDrag.resizePreview ? rowIndexById.get(barDrag.resizePreview.activityId) : null
  const linkTargetId = barDrag.drag && barDrag.drag.kind === 'link' ? barDrag.drag.targetId : null
  const linkTargetRowIndex = linkTargetId ? rowIndexById.get(linkTargetId) : null

  return (
    <svg
      data-gantt-timeline
      className={cn('absolute overflow-hidden', barDrag.drag ? 'cursor-default' : panning ? 'cursor-grabbing' : 'cursor-grab')}
      // ui-drift: dynamic
      style={{ left: gridWidth + scrollLeft, top: scrollTop, width, height: viewportHeight }}
      width={width}
      height={viewportHeight}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      <g transform={`translate(${-scrollLeft},${-scrollTop})`}>
        {(zoom === 'day' || zoom === 'week') && days.filter((d) => isWeekend(d.iso)).map((d) => (
          <rect key={d.day} x={d.day * pxPerDay} y={contentTop} width={pxPerDay} height={viewportHeight} className="fill-muted/60" />
        ))}
        {tiers.bottom.map((tk) => (
          <line key={tk.key} x1={tk.x} x2={tk.x} y1={contentTop} y2={contentBottom} className="stroke-border" strokeWidth={1} />
        ))}
        {virtualItems.map((vi) => {
          const r = rows[vi.index]
          const y = rowY(vi.index)
          return (
            <g key={r.node.activity.id}>
              {selected.has(r.node.activity.id) && <rect x={scrollLeft} y={y} width={width} height={ROW_H} className="fill-accent" />}
              <line x1={scrollLeft} x2={scrollLeft + width} y1={y + ROW_H} y2={y + ROW_H} className="stroke-border" strokeWidth={1} />
            </g>
          )
        })}
        <TodayLine x={xOf(today, range.start, pxPerDay) + pxPerDay / 2} y1={contentTop} y2={contentBottom} />
        {detail.schedule.statusDate && (
          <TodayLine dashed x={xOf(detail.schedule.statusDate, range.start, pxPerDay) + pxPerDay / 2} y1={contentTop} y2={contentBottom} />
        )}

        {virtualItems.map((vi) => {
          const a = rows[vi.index].node.activity
          return (
            <ActivityBar
              key={a.id}
              activity={a}
              top={rowY(vi.index)}
              range={range}
              pxPerDay={pxPerDay}
              selected={selected.has(a.id)}
              showBaseline={showBaseline}
              showCriticalPath={showCriticalPath}
              canEdit={canEdit}
              visibleOrder={visibleOrder}
            />
          )
        })}

        <DependencyLayer
          dependencies={detail.dependencies}
          byId={byId}
          externalById={externalById}
          tree={tree}
          collapsedIds={collapsedIds}
          showCriticalPath={showCriticalPath}
          visibleFrom={virtualItems.length ? virtualItems[0].index : 0}
          visibleTo={virtualItems.length ? virtualItems[virtualItems.length - 1].index : -1}
        />

        {/* BUG-SCH-UI2-01 fix: ghosts now paint AFTER the dependency links (they used to paint
            before), so a ghost's own (now hit-testable, `fill-transparent`) box always wins pointer
            hits over a link's invisible 6px-wide hit-stroke where a link visually terminates inside
            the ghost — which, for a link routed straight into the ghost's box, is anywhere along the
            ghost's full width at its row's centre line. Ghosts sit on a subset of rows and never
            overlap each other, so painting them last has no other visual effect. */}
        {virtualItems.map((vi) => {
          const a = rows[vi.index].node.activity
          const ghosts = ghostsByRow.get(a.id)
          if (!ghosts || ghosts.length === 0) return null
          const top = rowY(vi.index)
          return (
            <React.Fragment key={`ghosts-${a.id}`}>
              {ghosts.map((ext) => {
                const box = barBox(ext, range.start, pxPerDay)
                return (
                  <GhostMarker
                    key={ext.id}
                    x={box.x}
                    width={box.width}
                    rowTop={top}
                    label={`${ext.scheduleName} › ${ext.name}`}
                    isMilestone={ext.isMilestone}
                    onClick={(e) => { e.stopPropagation(); onGhostClick(ext) }}
                    onPointerEnter={(e) => setHover({ kind: 'ghost', id: ext.id, x: e.clientX, y: e.clientY })}
                    onPointerLeave={() => setHover(null)}
                  />
                )
              })}
            </React.Fragment>
          )
        })}

        {barDrag.resizePreview && resizeRowTop != null && (() => {
          const top = rowY(resizeRowTop)
          const y = top + (ROW_H - BAR_H) / 2
          const { x, width: w, label } = barDrag.resizePreview
          return (
            <React.Fragment key="resize-preview">
              <rect x={x} y={y} width={w} height={BAR_H} rx={3} className="fill-none stroke-primary stroke-2 pointer-events-none" strokeDasharray="4 3" />
              <text x={x + w + 6} y={y + BAR_H / 2} dominantBaseline="middle" className="text-xs fill-primary pointer-events-none select-none">{label}</text>
            </React.Fragment>
          )
        })()}

        {barDrag.linkPreview && (
          <React.Fragment key="link-preview">
            <line
              x1={barDrag.linkPreview.x1} y1={barDrag.linkPreview.y1}
              x2={barDrag.linkPreview.x2} y2={barDrag.linkPreview.y2}
              className="stroke-primary pointer-events-none" strokeWidth={2} strokeDasharray="4 3"
            />
            {barDrag.linkPreview.targetBox && linkTargetRowIndex != null ? (
              <rect
                x={barDrag.linkPreview.targetBox.x - 2}
                y={rowY(linkTargetRowIndex) + 3}
                width={barDrag.linkPreview.targetBox.width + 4}
                height={ROW_H - 6}
                rx={4}
                className="fill-none stroke-primary stroke-2 pointer-events-none"
              />
            ) : barDrag.linkPreview.refused ? (
              <text
                x={barDrag.linkPreview.x2 + 10} y={barDrag.linkPreview.y2} dominantBaseline="middle"
                className="text-xs fill-destructive pointer-events-none select-none"
              >
                {t('Schedule_link_drop_refused')}
              </text>
            ) : null}
          </React.Fragment>
        )}
      </g>
    </svg>
  )
}

// Memoised as a defence in depth alongside `GanttHoverProvider`: a hover change never
// reaches this component's props at all (it only ever reads the hover *dispatcher*), but
// `GanttView` still re-renders for other reasons (scroll, selection, zoom), and `memo`
// skips this component's own render when none of those actually changed its props.
export default React.memo(GanttTimeline)
